import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Paths, File as ExpoFile } from 'expo-file-system';
import styled from 'styled-components/native';
import { Platform, View } from 'react-native';
import SignatureScreen from 'react-native-signature-canvas';
import ReactSignatureCanvas from 'react-signature-canvas';

import FormElementQuestion from './components/FormElementQuestion';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { UploadFile } from '../../../api/upload';
import elementIDs from '../../../../elementIDs';
import api from '../../../api/api';
import { Caption } from '../../../components/Typography';

const StyledDigitalSignatureView = styled.View`
  width: 100%;
  height: 300px;
  border: solid 1px #ccc;
  border-radius: 10px;
`;

const StyledClearAction = styled.Text`
  margin: 15px 0;
  text-decoration: underline;
  text-align: right;
  font-size: 17px;
`;

const StyledWebSignatureContainer = styled.View`
  align-self: center;
`;

const StyledWebSignatureBox = styled.View`
  width: 100%;
  align-items: center;
  justify-content: center;
  border: solid 1px #ccc;
  border-radius: 10px;
`;

const StyledSignatureImage = styled.Image`
  height: ${Platform.OS === 'web' ? 180 : 300}px;
  width: ${Platform.OS === 'web' ? '400px' : 'auto'};
`;

const StyledClearContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

enum DigitalSignatureSigner {
  StudyParticipant = 'Study Participant',
  StudyCoordinator = 'Study Coordinator'
}

interface DigitalSignatureConfig {
  outputImageType?: string;
  allowedSigner: DigitalSignatureSigner;
  hideElement: boolean;
}

type DigitalSignatureProps<T> = FormElementProps<T, DigitalSignatureConfig>;

export const DigitalSignature = <T extends object>({
  element
}: DigitalSignatureProps<T>) => {
  const key = (element.dataSource as keyof T) || element.key;
  const { formValues, setFormValue, setIsScrollEnabled } =
    useDynamicFormContext<T, DigitalSignatureConfig>();
  const signatureScreenRef = useRef<any>(null);
  const signatureEdited = useRef<any>();
  const { config } = element;
  const [signatureImageUrl, setSignatureImageUrl] = useState<
    string | undefined
  >();
  const forStudyParticipant = useMemo(
    () => config?.allowedSigner !== DigitalSignatureSigner.StudyCoordinator,
    [config?.allowedSigner]
  );
  const resizeCanvas = useCallback(() => {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    if (signatureScreenRef?.current) {
      signatureScreenRef.current.getCanvas().width =
        signatureScreenRef?.current?.getCanvas()?.offsetWidth * ratio;
      signatureScreenRef.current.getCanvas().height =
        signatureScreenRef?.current?.getCanvas()?.offsetHeight * ratio;
      signatureScreenRef.current
        ?.getCanvas()
        .getContext('2d')
        .scale(ratio, ratio);
      signatureScreenRef?.current?.fromData(
        signatureScreenRef?.current?.toData()
      );
    }
  }, []);

  const clearSignaturePad = () => {
    signatureEdited.current = true;
    setSignatureImageUrl(undefined);
    setFormValue(key, {} as any, element);
    if (Platform.OS === 'web') {
      signatureScreenRef.current?.clear();
      resizeCanvas();
    } else {
      signatureScreenRef.current?.clearSignature();
    }
  };

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  useEffect(() => {
    (async () => {
      if (signatureEdited.current === true) return;
      if (formValues[key]) {
        const url = (formValues[key] as any)?.url;
        if (url) {
          const accessToken = api.accessToken;
          setSignatureImageUrl(url + '&token=' + accessToken);
          console.log(`MAMA`, url + '&token=' + accessToken);
        }
      }
    })();
  }, [formValues, key]);

  return config?.hideElement ? (
    <></>
  ) : (
    <View testID={elementIDs.signatureFieldID}>
      <FormElementQuestion element={element} />
      {Platform.OS === 'web' ? (
        <StyledWebSignatureContainer>
          {forStudyParticipant && (
            <StyledClearContainer>
              {signatureImageUrl && (
                <Caption color='dark'>
                  Clear existing signature to re-sign.
                </Caption>
              )}
              <StyledClearAction onPress={clearSignaturePad}>
                Clear
              </StyledClearAction>
            </StyledClearContainer>
          )}
          <StyledWebSignatureBox
            pointerEvents={forStudyParticipant ? undefined : 'none'}
          >
            {signatureImageUrl ? (
              <StyledSignatureImage source={{ uri: signatureImageUrl }} />
            ) : (
              <ReactSignatureCanvas
                canvasProps={{ style: { width: 400, height: 180 } }}
                maxWidth={2}
                minDistance={1}
                clearOnResize={false}
                ref={signatureScreenRef}
                throttle={16}
                onEnd={() => {
                  const image = signatureScreenRef.current?.toDataURL();
                  signatureEdited.current = true;
                  setFormValue(key, `${image}` as any, element);
                }}
              />
            )}
          </StyledWebSignatureBox>
        </StyledWebSignatureContainer>
      ) : (
        <>
          {forStudyParticipant && (
            <StyledClearContainer>
              {signatureImageUrl && (
                <Caption color='dark'>
                  Clear existing signature to re-sign.
                </Caption>
              )}
              <StyledClearAction
                testID={elementIDs.signatureClearButtonID}
                onPress={clearSignaturePad}
              >
                Clear
              </StyledClearAction>
            </StyledClearContainer>
          )}
          <StyledDigitalSignatureView
            pointerEvents={forStudyParticipant ? undefined : 'none'}
          >
            {signatureImageUrl ? (
              <StyledSignatureImage source={{ uri: signatureImageUrl }} />
            ) : (
              <SignatureScreen
                ref={signatureScreenRef}
                imageType='image/png'
                onBegin={() => setIsScrollEnabled(false)}
                onEnd={() => {
                  setIsScrollEnabled(true);
                  signatureScreenRef.current?.readSignature();
                }}
                onOK={(image: string) => {
                  signatureEdited.current = true;
                  setFormValue(key, image as any, element);
                }}
              />
            )}
          </StyledDigitalSignatureView>
        </>
      )}
    </View>
  );
};

const uploadSignature = async (value: string) => {
  // handle case where form sends the file JSON data
  if (typeof value !== 'string') return value;
  const uploadFile = new UploadFile('files/presign/form-signatures');
  const file = await getFileFromUri(value, 'signature.png');
  const results = await uploadFile.upload(file, {
    presignBody: { mimeType: 'image/png' }
  });
  if (!results?.presignedResult) throw new Error('Failed to upload');

  return {
    url: results.presignedResult.destinationUrl,
    name: file.name,
    size: file.size,
    timestamp: new Date()
  };
};

const getFileFromUri = async (uri: string, filename: string) => {
  const type = `image/jpeg`;
  let path = uri;
  let file: ExpoFile | undefined = undefined;
  if (Platform.OS === 'android') {
    /**
     * This is a workaround for Android, in order to get blob via fetch
     * as using fetch directly with the given uri from the component
     * results on "Network request failed" error.
     * Similar issues where base64 to blob fails on Android:
     * https://stackoverflow.com/q/65587492
     * https://stackoverflow.com/a/66295949
     */
    file = new ExpoFile(Paths.cache, filename);
    file.write(uri.replace('data:image/png;base64,', ''), {
      encoding: 'base64'
    });
    path = file.uri;
  }
  const response = await fetch(path);
  const blob = await response.blob();
  if (Platform.OS === 'android') {
    // Delete local file after obtaining blob
    file?.delete();
  }
  return new File([blob], filename, { type });
};

export const DigitalSignaturePlugin: FormElementPlugin<
  any,
  DigitalSignatureConfig
> = {
  name: 'Digital Signature',
  type: 'DigitalSignature',
  dataTypes: ['jsonb'],
  onBeforeSave: uploadSignature,
  element: DigitalSignature as React.FC<
    FormElementProps<any, DigitalSignatureConfig>
  >,
  validate(element, formValues) {
    // Workaround for disabling the `Transition` button when the element is `required`.
    // Since null values can’t be passed to clear the field, an empty object is used instead.
    if (element.required) {
      const value = (formValues as any)[element.dataSource || element.key];
      return (
        value &&
        (typeof value === 'string'
          ? value.length > 0
          : Object.keys(value).length > 0)
      );
    }
    return true;
  }
};
